/**
 * Chat Availability (NT-1041)
 *
 * Liquid-theme port of the nativepath-v2 headless chat-blocking behavior.
 * Decides whether the Gorgias live chat is available right now and, when it is
 * not, shows an offline modal instead of opening the widget.
 *
 * Unavailability signals (highest precedence first) — mirrors
 * app/lib/utils/chatAvailability.ts:
 *   1. Holidays          full-day closures (nativepath_holidays)
 *   2. Business hours    Mon–Fri 8 AM–5 PM Central Time
 *   3. Dated events      one-off closures on a specific date (nativepath_events)
 *   4. Weekly events     recurring closures on a weekday   (nativepath_events)
 *
 * The schedule is rendered server-side by snippets/chat-schedule.liquid and
 * read from the #chat-schedule-data <script> tag. When it is empty or invalid
 * we fall back to STATIC_SCHEDULE below, which mirrors
 * app/config/chat/chatUnavailabilitySchedule.ts (CHAT_UNAVAILABILITY_SCHEDULE).
 *
 * Two interception points, mirroring the headless app:
 *   - Our own Live Chat trigger(s) ([data-open-chat]) → app/hooks/useOpenChat.ts
 *   - The Gorgias floating launcher (widget:opened)   → ChatAvailabilityGuard.tsx
 */
(function () {
  'use strict';

  if (typeof window === 'undefined') {
    return;
  }

  // ---------------------------------------------------------------------------
  // Config (ports chatUnavailabilitySchedule.ts + chatAvailability.ts)
  // ---------------------------------------------------------------------------

  var SUPPORT_TIMEZONE = 'America/Chicago';
  var BUSINESS_HOURS_START_HOUR = 8;
  var BUSINESS_HOURS_END_HOUR = 17;
  var MINUTES_PER_HOUR = 60;

  var DEFAULT_AFTER_HOURS_TITLE =
    'Thanks for reaching out! Our team is currently offline.';
  var DEFAULT_AFTER_HOURS_MESSAGE =
    'Our support hours are Monday–Friday, 8 AM–5 PM Central Time. We look forward to assisting you—please connect with us again during our regular business hours.';
  var DEFAULT_TEMPORARY_TITLE = 'We’re temporarily unavailable.';
  var DEFAULT_HOLIDAY_TITLE = 'We’re closed for the holiday.';
  var DEFAULT_EVENT_MESSAGE =
    'We’re away for a brief team event and will be back soon. Thanks for your patience!';
  var DEFAULT_HOLIDAY_MESSAGE =
    'We’re closed today in observance of the holiday. We look forward to assisting you when we return.';

  /**
   * Static fallback used when the metaobjects are empty or fail to render.
   * Mirrors CHAT_UNAVAILABILITY_SCHEDULE in nativepath-v2. The metaobject data
   * (snippets/chat-schedule.liquid) is the source of truth; this only exists so
   * the chat never opens 24/7 if the schedule fails to load.
   */
  var STATIC_SCHEDULE = {
    weeklyEvents: [
      {
        id: 'monday-culture-call',
        dayOfWeek: 1,
        start: { hour: 11, minute: 0 },
        end: { hour: 12, minute: 0 },
        message: DEFAULT_EVENT_MESSAGE
      }
    ],
    dateEvents: [],
    holidays: [
      { id: 'new-years-day', date: '01-01' },
      { id: 'veterans-day', date: '11-11' },
      { id: 'christmas-eve', date: '12-24' },
      { id: 'christmas-day', date: '12-25' },
      { id: 'mlk-day', date: '01-19' },
      { id: 'memorial-day', date: '05-25' },
      { id: 'independence-day-observed', date: '07-03' },
      { id: 'labor-day', date: '09-07' },
      { id: 'thanksgiving', date: '11-26' }
    ]
  };

  // ---------------------------------------------------------------------------
  // Schedule transform (ports chatScheduleTransformer.ts)
  // ---------------------------------------------------------------------------

  var TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;
  var MONTH_DAY_PATTERN = /^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
  var FULL_DATE_PATTERN = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

  function parseTime(value) {
    if (!value) return undefined;
    var match = TIME_PATTERN.exec(String(value));
    if (!match) return undefined;
    return { hour: Number(match[1]), minute: Number(match[2]) };
  }

  function parseDayOfWeek(value) {
    if (value === null || value === undefined || value === '') return undefined;
    var parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed < 0 || parsed > 6) return undefined;
    return parsed;
  }

  function parseFullDate(value) {
    if (!value) return undefined;
    return FULL_DATE_PATTERN.test(String(value)) ? String(value) : undefined;
  }

  function parseMonthDay(value) {
    if (!value) return undefined;
    return MONTH_DAY_PATTERN.test(String(value)) ? String(value) : undefined;
  }

  /**
   * Converts the raw #chat-schedule-data JSON into the schedule shape consumed
   * by getChatUnavailability. Malformed entries are skipped, never thrown.
   */
  function transformSchedule(raw) {
    var weekly = [];
    var dated = [];
    var holidays = [];

    var events = (raw && raw.events) || [];
    for (var i = 0; i < events.length; i++) {
      var node = events[i] || {};
      var id = (node.id && String(node.id).trim()) || 'event';
      var start = parseTime(node.startTime);
      var end = parseTime(node.endTime);
      if (!start || !end) continue;

      var dayOfWeek = parseDayOfWeek(node.dayOfWeek);
      var eventDate = parseFullDate(node.eventDate);

      // Ambiguous config — skip, like the headless transformer.
      if (dayOfWeek !== undefined && eventDate) continue;

      if (dayOfWeek !== undefined) {
        weekly.push({
          id: id,
          dayOfWeek: dayOfWeek,
          start: start,
          end: end,
          message: DEFAULT_EVENT_MESSAGE
        });
      } else if (eventDate) {
        dated.push({
          id: id,
          date: eventDate,
          start: start,
          end: end,
          message: DEFAULT_EVENT_MESSAGE
        });
      }
    }

    var holidayNodes = (raw && raw.holidays) || [];
    for (var j = 0; j < holidayNodes.length; j++) {
      var hNode = holidayNodes[j] || {};
      var date = parseMonthDay(hNode.date);
      if (!date) continue;
      holidays.push({
        id: (hNode.id && String(hNode.id).trim()) || 'holiday',
        date: date,
        message: DEFAULT_HOLIDAY_MESSAGE
      });
    }

    return { weeklyEvents: weekly, dateEvents: dated, holidays: holidays };
  }

  function isScheduleEmpty(schedule) {
    return (
      schedule.weeklyEvents.length === 0 &&
      schedule.dateEvents.length === 0 &&
      schedule.holidays.length === 0
    );
  }

  function loadSchedule() {
    var el = document.getElementById('chat-schedule-data');
    if (!el) return STATIC_SCHEDULE;
    var raw;
    try {
      raw = JSON.parse(el.textContent || '{}');
    } catch (error) {
      return STATIC_SCHEDULE;
    }
    var schedule = transformSchedule(raw);
    return isScheduleEmpty(schedule) ? STATIC_SCHEDULE : schedule;
  }

  // ---------------------------------------------------------------------------
  // Availability (ports chatAvailability.ts)
  // ---------------------------------------------------------------------------

  var WEEKDAY_MAP = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

  /**
   * Extracts year/month/day/weekday/minute parts of `now` as observed in
   * Central Time via Intl, so DST transitions and the visitor's own timezone
   * are handled correctly.
   */
  function getCentralDateParts(now) {
    var formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: SUPPORT_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      weekday: 'short',
      hour12: false
    });
    var parts = {};
    formatter.formatToParts(now).forEach(function (part) {
      parts[part.type] = part.value;
    });

    var hour = Number(parts.hour) % 24; // Intl can return "24" at midnight
    var minute = Number(parts.minute);

    return {
      year: Number(parts.year),
      month: Number(parts.month),
      day: Number(parts.day),
      dayOfWeek: WEEKDAY_MAP[parts.weekday] || 0,
      minutesSinceMidnight: hour * MINUTES_PER_HOUR + minute
    };
  }

  function dayPartToMinutes(part) {
    return part.hour * MINUTES_PER_HOUR + part.minute;
  }

  function pad2(value) {
    return String(value).padStart(2, '0');
  }

  function isoDate(parts) {
    return parts.year + '-' + pad2(parts.month) + '-' + pad2(parts.day);
  }

  function monthDay(parts) {
    return pad2(parts.month) + '-' + pad2(parts.day);
  }

  function isWithinWindow(current, start, end) {
    return current >= dayPartToMinutes(start) && current < dayPartToMinutes(end);
  }

  function isWithinBusinessWindow(parts) {
    var isWeekday = parts.dayOfWeek >= 1 && parts.dayOfWeek <= 5;
    if (!isWeekday) return false;
    var start = BUSINESS_HOURS_START_HOUR * MINUTES_PER_HOUR;
    var end = BUSINESS_HOURS_END_HOUR * MINUTES_PER_HOUR;
    return (
      parts.minutesSinceMidnight >= start && parts.minutesSinceMidnight < end
    );
  }

  /**
   * Determines whether the chat is available right now and, if not, the reason
   * and copy to display. Holidays > business hours > dated events > weekly
   * events (matches the headless precedence).
   */
  function getChatUnavailability(now, schedule) {
    var parts = getCentralDateParts(now);
    var todayMonthDay = monthDay(parts);

    var holiday = schedule.holidays.filter(function (entry) {
      return entry.date === todayMonthDay;
    })[0];
    if (holiday) {
      return {
        available: false,
        reason: 'holiday',
        title: DEFAULT_HOLIDAY_TITLE,
        message: holiday.message || DEFAULT_HOLIDAY_MESSAGE
      };
    }

    if (!isWithinBusinessWindow(parts)) {
      return {
        available: false,
        reason: 'after-hours',
        title: DEFAULT_AFTER_HOURS_TITLE,
        message: DEFAULT_AFTER_HOURS_MESSAGE
      };
    }

    var today = isoDate(parts);
    var datedEvent = schedule.dateEvents.filter(function (event) {
      return (
        event.date === today &&
        isWithinWindow(parts.minutesSinceMidnight, event.start, event.end)
      );
    })[0];
    if (datedEvent) {
      return {
        available: false,
        reason: 'event',
        title: DEFAULT_TEMPORARY_TITLE,
        message: datedEvent.message
      };
    }

    var weeklyEvent = schedule.weeklyEvents.filter(function (event) {
      return (
        event.dayOfWeek === parts.dayOfWeek &&
        isWithinWindow(parts.minutesSinceMidnight, event.start, event.end)
      );
    })[0];
    if (weeklyEvent) {
      return {
        available: false,
        reason: 'event',
        title: DEFAULT_TEMPORARY_TITLE,
        message: weeklyEvent.message
      };
    }

    return { available: true };
  }

  // ---------------------------------------------------------------------------
  // Gorgias helpers (ports chat.ts)
  // ---------------------------------------------------------------------------

  var GORGIAS_LOADED_EVENT = 'gorgias-widget-loaded';
  var WIDGET_OPENED_EVENT = 'widget:opened';
  var HIDE_STYLE_ID = 'chat-availability-guard-hide-gorgias';

  /**
   * Hides the Gorgias chat panel (not the launcher bubble) while our offline
   * modal is shown, so calling `close()` doesn't race the open animation and
   * flash the panel. Mirrors the CSS approach in ChatAvailabilityGuard.tsx.
   */
  var HIDE_CSS =
    'iframe[id*="chat-window"],' +
    'iframe[id*="chat-input"],' +
    'iframe[class*="gorgias-chat-key"]{' +
    'visibility:hidden !important;opacity:0 !important;pointer-events:none !important;}';

  function getGorgias() {
    return window.GorgiasChat;
  }

  function setGorgiasPanelHidden(hidden) {
    var existing = document.getElementById(HIDE_STYLE_ID);
    if (hidden) {
      if (existing) return;
      var style = document.createElement('style');
      style.id = HIDE_STYLE_ID;
      style.textContent = HIDE_CSS;
      document.head.appendChild(style);
    } else if (existing) {
      existing.remove();
    }
  }

  /**
   * Opens the Gorgias widget, loading/initialising it on demand. The theme
   * loads Gorgias via a Shopify app-embed block, so the SDK is usually already
   * present; when a click lands before it finishes loading we wait for the
   * `gorgias-widget-loaded` event (with a short poll fallback).
   */
  function openChat() {
    var g = getGorgias();
    if (g && typeof g.open === 'function') {
      try {
        g.open();
      } catch (error) {
        /* SDK not ready — fall through to the waiter below. */
      }
      return;
    }

    var opened = false;
    var attempts = 0;
    var open = function () {
      if (opened) return;
      var chat = getGorgias();
      if (chat && typeof chat.open === 'function') {
        opened = true;
        window.removeEventListener(GORGIAS_LOADED_EVENT, open);
        try {
          chat.open();
        } catch (error) {
          /* give up quietly */
        }
      }
    };
    window.addEventListener(GORGIAS_LOADED_EVENT, open);
    var poll = window.setInterval(function () {
      attempts += 1;
      open();
      if (opened || attempts >= 40) {
        window.clearInterval(poll);
        window.removeEventListener(GORGIAS_LOADED_EVENT, open);
      }
    }, 250);
  }

  // ---------------------------------------------------------------------------
  // Offline modal (ports ChatOfflineModal.tsx)
  // ---------------------------------------------------------------------------

  var activeModal = null;

  function closeModal() {
    if (activeModal) {
      activeModal.remove();
      activeModal = null;
    }
    document.body.classList.remove('chat-offline-open');
    document.removeEventListener('keydown', onModalKeydown);
    setGorgiasPanelHidden(false);
  }

  function onModalKeydown(event) {
    if (event.key === 'Escape') closeModal();
  }

  function showOfflineModal(title, message) {
    closeModal();

    var logoUrl = '';
    var dataEl = document.getElementById('chat-schedule-data');
    if (dataEl) logoUrl = dataEl.getAttribute('data-logo') || '';

    var overlay = document.createElement('div');
    overlay.className = 'chat-offline-modal';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'chat-offline-modal-title');
    overlay.setAttribute('data-testid', 'chat-offline-modal');

    var dialog = document.createElement('div');
    dialog.className = 'chat-offline-modal__dialog';

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'chat-offline-modal__close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.innerHTML = '&times;';
    closeBtn.addEventListener('click', closeModal);

    if (logoUrl) {
      var logo = document.createElement('img');
      logo.className = 'chat-offline-modal__logo';
      logo.src = logoUrl;
      logo.alt = 'NativePath';
      logo.width = 200;
      dialog.appendChild(logo);
    }

    var heading = document.createElement('h3');
    heading.className = 'chat-offline-modal__title';
    heading.id = 'chat-offline-modal-title';
    heading.textContent = title || '';

    var body = document.createElement('p');
    body.className = 'chat-offline-modal__message';
    body.textContent = message || '';

    var footerBtn = document.createElement('button');
    footerBtn.type = 'button';
    footerBtn.className = 'chat-offline-modal__button';
    footerBtn.textContent = 'Close';
    footerBtn.addEventListener('click', closeModal);

    dialog.appendChild(closeBtn);
    dialog.appendChild(heading);
    dialog.appendChild(body);
    dialog.appendChild(footerBtn);
    overlay.appendChild(dialog);

    overlay.addEventListener('click', function (event) {
      if (event.target === overlay) closeModal();
    });

    document.body.appendChild(overlay);
    document.body.classList.add('chat-offline-open');
    document.addEventListener('keydown', onModalKeydown);
    activeModal = overlay;
    footerBtn.focus();
  }

  // ---------------------------------------------------------------------------
  // Entry points
  // ---------------------------------------------------------------------------

  var schedule = loadSchedule();

  /** Our own Live Chat triggers — ports useOpenChat.ts. */
  function handleOpenChat() {
    var status = getChatUnavailability(new Date(), schedule);
    if (status.available) {
      openChat();
      return;
    }
    showOfflineModal(status.title, status.message);
  }

  function bindTriggers() {
    var triggers = document.querySelectorAll('[data-open-chat]');
    for (var i = 0; i < triggers.length; i++) {
      triggers[i].addEventListener('click', function (event) {
        event.preventDefault();
        handleOpenChat();
      });
    }
  }

  /**
   * Intercepts the Gorgias floating launcher — ports ChatAvailabilityGuard.tsx.
   * When the visitor opens chat via Gorgias's own bubble during an
   * unavailability window, close the widget and show the offline modal instead.
   */
  function installLauncherGuard() {
    var handler = null;
    var initStarted = false;
    var attempts = 0;

    var onWidgetOpened = function () {
      var status = getChatUnavailability(new Date(), schedule);
      if (status.available) return;
      var g = getGorgias();
      if (g && typeof g.close === 'function') {
        try {
          g.close();
        } catch (error) {
          /* ignore */
        }
      }
      setGorgiasPanelHidden(true);
      showOfflineModal(status.title, status.message);
    };

    var registerListener = function (g) {
      if (handler) return;
      var onFn;
      try {
        onFn = g.on; // reading `.on` before init() resolves can throw
      } catch (error) {
        return;
      }
      if (!onFn) return;
      onFn.call(g, WIDGET_OPENED_EVENT, onWidgetOpened);
      handler = onWidgetOpened;
    };

    var trySubscribe = function () {
      if (handler) return;
      var g = getGorgias();
      if (!g) return;
      registerListener(g);
      if (handler || initStarted) return;
      initStarted = true;
      try {
        var result = g.init && g.init();
        if (result && typeof result.then === 'function') {
          result
            .then(function () {
              registerListener(getGorgias());
            })
            .catch(function () {
              initStarted = false;
            });
        }
      } catch (error) {
        initStarted = false;
      }
    };

    window.addEventListener(GORGIAS_LOADED_EVENT, trySubscribe);
    trySubscribe();
    var poll = window.setInterval(function () {
      attempts += 1;
      if (handler || attempts >= 60) {
        window.clearInterval(poll);
        window.removeEventListener(GORGIAS_LOADED_EVENT, trySubscribe);
        return;
      }
      trySubscribe();
    }, 500);
  }

  function init() {
    bindTriggers();
    installLauncherGuard();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Exposed for debugging / e2e assertions.
  window.NativePathChat = {
    getChatUnavailability: getChatUnavailability,
    getSchedule: function () {
      return schedule;
    }
  };
})();
